import {Service} from "vue-typedi";
import {productsApiClient} from "@/configuration/api-client";
import { Product } from "@/model/product/Product";
import { SerializationHelper } from "@/helpers/SerializationHelper";
import {AxiosRequestConfig} from "axios";
import { Traceability } from "@/model/product/Traceability";

@Service()
export class ProductService {

    static PRODUCTS_ENDPOINT: string = '/products';
    static GET_PRODUCTS_BY_ZONE: string = '/products';
    static GET_PRODUCTS_BY_ZONES: string = '/products/zones';
    static GET_TRACEABILITY_URL: string = "/traceability";
    static UPDATE_ORIGIN_URL: string = "/traceability/origins";
    private static readonly STORE_ID: string = "storeId";

    public getProducts(zoneCode: string, cug?: string): Promise<Product[]> {
        const params = {
            storeId: sessionStorage.getItem(ProductService.STORE_ID),
            zoneCode: zoneCode,
            cug: cug
        };

        return productsApiClient.get(ProductService.GET_PRODUCTS_BY_ZONE, { params: params })
            .then((response: any) => {
                const data: Product[] = response?.data ?? [];
                return Promise.resolve(SerializationHelper.toArrayInstanceFromJsonObjArray(() => new Product(), data));
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public getProductsByZones(zoneCodes: string[], cug?: string): Promise<Product[]> {
        const params = new URLSearchParams();
        params.append('storeId', sessionStorage.getItem(ProductService.STORE_ID));
        zoneCodes.forEach(zoneCode => params.append('zoneCodes', zoneCode));
        if (cug != null && cug !== "") {
          params.append("cug", cug);
        }

        return productsApiClient.get(ProductService.GET_PRODUCTS_BY_ZONES, { params: params })
            .then((response: any) => {
                const data: Product[] = response?.data ?? [];
                return Promise.resolve(SerializationHelper.toArrayInstanceFromJsonObjArray(() => new Product(), data));
            }).catch(error => {
                return Promise.reject(error);
            });
    }


    public getTraceability(context: string): Promise<Traceability> {
        return productsApiClient.get(ProductService.PRODUCTS_ENDPOINT + ProductService.GET_TRACEABILITY_URL, { params: { context: context } })
            .then((response: any) => {
                const data: Traceability = response?.data ?? {};
                return Promise.resolve(data);
            }).catch(error => {
                return Promise.reject(error);
            });
    }

    public updateProduct(product: Product): Promise<Product> {
        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        return productsApiClient.put(ProductService.PRODUCTS_ENDPOINT, product, config)
            .then((response: {data: Product}) => {
                const data: Product = response && response.data;
                return Promise.resolve(SerializationHelper.toInstanceFromJsonObj(new Product(), data));
            }).catch(error => {
                return Promise.reject(error);
            })
    }

    public updateProductOutOfStockStatus(zoneCode: string, cug: string): Promise<Product> {
        if (cug == null) {
            return Promise.reject("cug should be provided to update product out of stock status");
        }
        if (zoneCode == null) {
            return Promise.reject("zone code should be provided to update product out of stock status");
        }

        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const requestBody = {
            storeId: sessionStorage.getItem(ProductService.STORE_ID),
            zoneCode: zoneCode,
            cug: cug
        };

        return productsApiClient.put(ProductService.PRODUCTS_ENDPOINT + "/out-of-stock", requestBody, config)
            .then((response: {data: Product}) => {
                const data: Product = response && response.data;
                return Promise.resolve(SerializationHelper.toInstanceFromJsonObj(new Product(), data));
            }).catch(error => {
                return Promise.reject(error);
            })
    }

    public updateProductOrigin(origin: string, zoneCode: string, cug: string): Promise<Product> {
        if (cug == null) {
            return Promise.reject("cug should be provided to update product out of stock status");
        }
        if (zoneCode == null) {
            return Promise.reject("zone code should be provided to update product out of stock status");
        }

        const config = {
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const requestBody = {
            storeId: sessionStorage.getItem(ProductService.STORE_ID),
            zoneCode: zoneCode,
            cug: cug,
            origin: origin
        };

        return productsApiClient.put(ProductService.PRODUCTS_ENDPOINT + ProductService.UPDATE_ORIGIN_URL, requestBody, config)
            .then((response: {data: Product}) => {
                const data: Product = response && response.data;
                return Promise.resolve(SerializationHelper.toInstanceFromJsonObj(new Product(), data));
            }).catch(error => {
                return Promise.reject(error);
            })
    }

    public getMassImportBackup(storeIds: string[], cugs: string[]) {
        const config: AxiosRequestConfig = {
            responseType: 'blob',
            timeout: 30000,
            params: {}
        }
        const requestBody = {
            storeIds: storeIds,
            cugs: cugs
        }
        return productsApiClient.post(ProductService.PRODUCTS_ENDPOINT + '/export-csv', requestBody, config)
    }

    public sendMassImport(file: File, storeIds: string[]): Promise<void> {
        const formData = new FormData();
        formData.append('importFile', file);
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            },
            params: {
                storeIds: storeIds.join(',')
            }
        }
        return productsApiClient.post(ProductService.PRODUCTS_ENDPOINT + '/import-csv', formData, config)
            .then(response => Promise.resolve())
            .catch(error => Promise.reject(error));
    }
}
